import fetchAPI from '../fetchAPI';
import type { ExtensionReportItem } from './callGetExtensionReports';

/** One parsed message as the extension summarised it — no text, only its shape. */
export interface DiagnosticsMessage {
  id: string;
  sent: boolean;
  timestamp: string;
  textLength: number;
  /** Salted per report: equal hashes mean equal text within this report only. */
  textHash: string;
  senderHash?: string;
  hasPhone: boolean;
}

export interface DiagnosticsParsed {
  source: 'interceptor' | 'dom';
  stats: { rows: number; inCount: number; outCount: number; parseErrorCount: number };
  messages: DiagnosticsMessage[];
}

/** The open chat, captured by the extension with text and names removed. */
/** The WhatsApp tab as a whole, captured whether or not a chat is open
 *  (extension `buildPageSnapshot`). No text, names or numbers: ids, roles
 *  and `data-testid`s only. */
export interface DiagnosticsPage {
  state: 'qr' | 'loading' | 'chat-list' | 'chat-open' | 'unknown';
  path: string;
  visible: boolean;
  focused: boolean;
  uptimeSec: number;
  counts: { chatListRows: number; messageRows: number; drawerOpen: boolean };
  selectors: { name: string; applicable: boolean; ok: boolean }[];
  interceptor: { ready: boolean; bufferedMessages: number };
  outline: string[];
  outlineTruncated: boolean;
}

export interface DiagnosticsSnapshot {
  /** 'cache-clear' when sent just before a cache clear. */
  trigger?: string;
  /** Present since Oct 2026 on every report from a WhatsApp tab, chat or not;
   *  null when no WhatsApp tab answered. */
  page?: DiagnosticsPage | null;
  /** Number of WhatsApp tabs open when the report was taken. */
  whatsAppTabs?: number;
  // The chat fields below are only there when a chat was open — read them
  // through `chatSnapshot()`, never directly.
  capturedAt: string;
  isGroup: boolean;
  rowCount: number;
  virtualizedCount: number;
  rowsHtml: string[];
  truncated: boolean;
  dom: DiagnosticsParsed | null;
  interceptor: (DiagnosticsParsed & { ready: boolean }) | null;
}

export interface DiagnosticsBreadcrumb {
  ts: string;
  action: string;
  data?: Record<string, unknown>;
}

/** Mirrors the extension's `SyncAttempt` (negotiator `src/storage/items.ts`). */
export interface DiagnosticsSyncAttempt {
  ts: string;
  phoneMasked: string;
  action: 'auto' | 'force';
  sent: number;
  serverStatus: number;
  serverMessage?: string;
  parserUsed: 'interceptor' | 'dom' | 'unknown';
  waWebVersion?: string | null;
}

export interface DiagnosticsReport extends ExtensionReportItem {
  kind: 'auto' | 'manual';
  referenceCode: string | null;
  diagnostics: DiagnosticsSnapshot | null;
  recentSyncAttempts: DiagnosticsSyncAttempt[] | null;
  recentBreadcrumbs: DiagnosticsBreadcrumb[] | null;
  environment: ExtensionReportItem['environment'] & {
    waWebVersion?: { version?: string; build?: string } | null;
  };
  /** Who sent it — resolved server-side from `userId`. */
  user: { id: string; name: string | null; email: string | null; companyName: string | null } | null;
}

/** GET /ext/report/:code — Super Admin only. Accepts "SR-7F3K2Q" or "7f3k2q". */
const callGetDiagnosticsReport = async (code: string): Promise<iResponse<DiagnosticsReport>> =>
  fetchAPI<DiagnosticsReport>({
    method: 'GET',
    route: `ext/report/${encodeURIComponent(code.trim())}`
  });

export default callGetDiagnosticsReport;

/** The chat snapshot, or null when the report has none — including a report
 *  sent with no chat open, whose `diagnostics` carries only `page` and
 *  `trigger`. */
export const chatSnapshot = (report: DiagnosticsReport): DiagnosticsSnapshot | null =>
  Array.isArray(report.diagnostics?.rowsHtml) ? report.diagnostics : null;
